import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import {
  ApiError,
  Card,
  CardHeader,
  CardSection,
  CondensedMedium,
  Flashy,
  Layout,
  LayoutItem,
  Suspense,
  useApiWithErrors,
  Well,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';

import { AccountBenchApi } from '../../api/account-bench-api';
import { CandidateApi } from '../../api/candidate-api';
import { DateUtil } from '../../utils/date-util';
import { TalentProfileEditForm } from '../TalentProfileEditForm/TalentProfileEditForm';
import { MlRecommendationsApi } from '../../api/ml-recommendations-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { CandidateAttributes } from '../CandidateAttributes/CandidateAttributes';
import { fullNameByIdSelector } from '../../redux/selectors/users';

import { OTFlex } from './OpportunityTalentStyles';
import { OpportunityTalentContextMenu } from './OpportunityTalentContextMenu';
import { OpportunityTalentRateMargin } from './OpportunityTalentRateMargin';

const { AllStatusLabels, CandidateRejectionLossCodesLabels } =
  CandidateOpportunitiesConst;

const allLabels: Record<string, string> = AllStatusLabels;

const lossCodeLabels: Record<string, string> =
  CandidateRejectionLossCodesLabels;

export const OpportunityTalentCandidate = ({
  children,
}: React.PropsWithChildren) => {
  const { id: opportunityId, viewId: candidateId } = useParams();
  if (!opportunityId) {
    throw new Error('No opportunityId provided');
  }
  if (!candidateId) {
    throw new Error('No candidateId provided');
  }

  const navigate = useNavigate();
  const usersById = useSelector(fullNameByIdSelector);

  const [showTalentProfileEditForm, setShowTalentProfileEditForm] =
    useState(false);

  const requests = useApiWithErrors(
    CandidateApi.readCandidate(candidateId),
    OpportunityLegacyApi.readOpportunityCandidates(opportunityId),
    OpportunityLegacyApi.readOpportunity(opportunityId),
    MlRecommendationsApi.readRecommendations(opportunityId),
    AccountBenchApi.readOpportunityBench({ opportunityId })
  );

  const [
    { data: candidate },
    { data: opportunityCandidates },
    { data: opportunity },
    { data: mlRecommendations },
    { data: benchResponse },
  ] = requests;

  const redirect = () => {
    void navigate(`/opportunity-detail/${opportunityId}/talent`);
  };

  if (requests.some(d => d instanceof ApiError)) {
    redirect();
    return;
  }

  const opportunityCandidate = opportunityCandidates.find(
    co => co.candidateId === candidateId
  );
  const contextMenuOpportunityCandidate = opportunityCandidate
    ? {
        ...opportunityCandidate,
        isMlRecommendation: false,
      }
    : undefined;

  const isBenchCandidate =
    !opportunityCandidate &&
    benchResponse.candidates.some(c => c.candidateId === candidateId);
  const mlRecommendation = opportunityCandidate
    ? undefined
    : mlRecommendations.find(rec => rec.candidateId === candidateId);

  if (!opportunityCandidate && !mlRecommendation && !isBenchCandidate) {
    redirect();
    return;
  }

  const candidateStatus =
    opportunityCandidate?.candidateStatus ??
    mlRecommendation?.candidateStatus ??
    null;

  const previousCandidateStatus =
    opportunityCandidate?.previousCandidateStatus ??
    mlRecommendation?.previousCandidateStatus;
  const rejectionLossCode =
    opportunityCandidate?.rejectionLossCode ?? mlRecommendation?.mlLossCode;
  const candidateStatusUpdatedAt =
    opportunityCandidate?.candidateStatusUpdatedAt ??
    mlRecommendation?.updatedAt;

  const passReason =
    candidateStatus === 'Rejected' && rejectionLossCode
      ? lossCodeLabels[rejectionLossCode]
      : undefined;

  return (
    <Suspense>
      <Card name="opportunity-talent-candidate">
        <CardHeader background="blue">
          <CandidateAttributes
            candidate={candidate}
            usersById={usersById}
            inlineBadges
          >
            <OpportunityTalentContextMenu
              name="OPPORTUNITY_TALENT_CANDIDATE_CONTEXT_MENU"
              candidate={candidate}
              currentCandidateStatus={candidateStatus}
              isBenchCandidate={isBenchCandidate}
              isMlRecommendation={!!mlRecommendation}
              opportunityCandidate={contextMenuOpportunityCandidate}
              opportunity={opportunity}
            />
          </CandidateAttributes>
        </CardHeader>

        {showTalentProfileEditForm ? (
          <TalentProfileEditForm
            name="TALENT_PROFILE_EDIT_FORM_FROM_CANDIDATE"
            candidate={candidate}
            onClose={() => {
              setShowTalentProfileEditForm(false);
              void AccountBenchApi.refreshOpportunityBench({ opportunityId });
            }}
          />
        ) : null}

        <CardSection>
          <Layout direction="vertical" verticalGutter="16px">
            {isBenchCandidate ? null : (
              <Well name="candidate-status-well">
                <Layout position="stretch space-between">
                  <OTFlex>
                    <Layout direction="horizontal" horizontalGutter="6px">
                      <LayoutItem>
                        <CondensedMedium maxLines={1}>
                          <Flashy bold>Rate & margin:</Flashy>
                        </CondensedMedium>
                      </LayoutItem>
                      <LayoutItem>
                        <OpportunityTalentRateMargin
                          opportunityCandidate={opportunityCandidate}
                          opportunity={opportunity}
                        />
                      </LayoutItem>
                    </Layout>
                  </OTFlex>
                  <OTFlex>
                    <CondensedMedium name="previous-status">
                      <Flashy bold>Previous status:</Flashy>{' '}
                      {previousCandidateStatus
                        ? allLabels[previousCandidateStatus]
                        : null}
                    </CondensedMedium>
                  </OTFlex>
                  <OTFlex>
                    <CondensedMedium name="status-last-updated">
                      <Flashy bold>Status last updated:</Flashy>{' '}
                      {DateUtil.displayDate(candidateStatusUpdatedAt)}
                    </CondensedMedium>
                  </OTFlex>
                </Layout>
                {passReason ? (
                  <CondensedMedium name="client-pass-reason">
                    <Flashy bold>Client-Pass:</Flashy> {passReason}
                  </CondensedMedium>
                ) : null}
              </Well>
            )}
            {children}
          </Layout>
        </CardSection>
      </Card>
    </Suspense>
  );
};
